// Self-hosted cookie-consent + analytics loader.
//
// Analytics (Google Analytics 4) must not load — no script, no cookies — until
// the visitor explicitly accepts. Essential cookies are exempt, but this public
// site sets none of its own beyond analytics, so gating GA is the whole job.
// The choice is persisted in localStorage; gtag.js is injected only on accept.

export const GA_ID = 'G-KHBE24FNGZ';
const STORAGE_KEY = 'cookie-consent';

// 'granted' | 'denied' | null (no choice made yet).
export const getConsent = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return null;
  }
};

const store = (value) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, value);
  } catch (e) {
    // localStorage unavailable (private mode / blocked) — consent simply won't
    // persist across loads. The banner will show again, which is acceptable.
  }
};

let gaLoaded = false;

// Inject gtag.js once and initialise GA4. Pageviews are sent manually per route
// (send_page_view: false) so single-page navigations are counted — see
// gatsby-browser's onRouteUpdate and trackPageView below.
export const loadAnalytics = () => {
  if (typeof window === 'undefined' || gaLoaded) return;
  gaLoaded = true;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('config', GA_ID, {
    anonymize_ip: true,
    send_page_view: false,
  });
};

export const trackPageView = () => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_location: window.location.href,
    page_title: document.title,
  });
};

// Best-effort removal of GA's cookies so a withdrawal leaves no analytics
// cookies behind (they are set on the registrable domain, e.g. .kinesis.org).
const clearAnalyticsCookies = () => {
  if (typeof document === 'undefined') return;
  const host = window.location.hostname;
  const parts = host.split('.');
  const domains = new Set(['', host, `.${host}`]);
  if (parts.length > 2) {
    const base = parts.slice(-2).join('.');
    domains.add(base);
    domains.add(`.${base}`);
  }
  document.cookie.split(';').forEach((entry) => {
    const name = entry.split('=')[0].trim();
    if (!/^(_ga|_gid|_gat)/.test(name)) return;
    domains.forEach((domain) => {
      const scope = domain ? `; domain=${domain}` : '';
      document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${scope}`;
    });
  });
};

export const grantConsent = () => {
  store('granted');
  loadAnalytics();
  trackPageView();
};

export const denyConsent = () => {
  const wasLoaded = gaLoaded;
  store('denied');
  clearAnalyticsCookies();
  // If analytics ran earlier this session (an accept later withdrawn), reload so
  // nothing keeps collecting once the choice flips to denied.
  if (wasLoaded) window.location.reload();
};
